import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Service } from '../types/api';

interface ServiceCardProps {
  service: Service;
  isFeatured?: boolean;
}

export function ServiceCard({ service, isFeatured = false }: ServiceCardProps) {
  const { t } = useTranslation(['services', 'audience']);

  return (
    <Link
      to={`/services/${service.slug}`}
      className="group block h-full focus-ring rounded-2xl"
    >
      <article className={`h-full p-6 glass-card rounded-2xl border border-white/[0.02] group-hover:bg-white/[0.07] group-hover:border-white/15 transition-all duration-300 hover:shadow-glow-cyan/20 flex flex-col justify-between ${isFeatured ? 'p-8 sm:p-10' : ''}`}>
        <div>
          {/* Accent indicator */}
          <div className="flex items-start justify-between mb-4">
            <div className="w-1 h-8 bg-gradient-to-b from-neon-cyan to-neon-purple rounded-full shadow-glow-cyan" />
            <ArrowRight className="w-5 h-5 text-text-muted group-hover:text-neon-cyan group-hover:translate-x-1 transition-all duration-200" />
          </div>

          {/* Service name */}
          <h3 className={`font-semibold text-text-primary mb-2 group-hover:text-neon-cyan transition-colors font-heading ${isFeatured ? 'text-2xl sm:text-3xl lg:text-4xl' : 'text-xl'}`}>
            {t(`services:${service.slug}.title`, { defaultValue: service.name })}
          </h3>

          {/* Description */}
          <p className={`text-text-secondary leading-relaxed ${isFeatured ? 'text-base sm:text-lg mb-8 line-clamp-3' : 'text-sm mb-4 line-clamp-2'}`}>
            {t(`services:${service.slug}.description`, { defaultValue: service.description })}
          </p>
        </div>

        {/* Audience tags */}
        {service.audience_types && service.audience_types.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-1.5">
            {service.audience_types.map((audience) => (
              <span
                key={audience}
                className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full bg-neon-purple/10 text-neon-purple border border-neon-purple/20 group-hover:bg-neon-cyan/10 group-hover:text-neon-cyan group-hover:border-neon-cyan/20 transition-colors"
              >
                {t(`audience:${audience}.title`, { defaultValue: audience })}
              </span>
            ))}
          </div>
        )}
      </article>
    </Link>
  );
}
